import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { Chip, Grid, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MissionEnumsLists, TaskEnums } from "../../../../common/constants";
import { TimeView } from "../../../common/core/Time";
import UrgencyColumn from "./UrgencyColumn";
import DriverColumn from "./DriverColumn";
import TaskProgressColumn from "./TaskProgressColumn";
import { useStation } from "../../../../providers/StationProvider";
import { truncate } from "../../../../common/helpers";
import { FormatLocationShort } from "../../../../common/maphelpers";

const useStyles = makeStyles()(
  (theme) => ({
    squareChip: {
      borderRadius: theme.spacing(1),
      height: "auto",
      minWidth: "96px",
      lineHeight: "16px",
      paddingTop: "8px",
      paddingBottom: "8px",
      justifyContent: "flex-start",
      "& .time": {
        fontSize: "12px",
        opacity: 0.75
      }
    },
    rejectedRow: {
      background: theme.palette.error.background,
      "&:hover": {
        background: `${theme.palette.error.backgroundFocus} !important`
      }
    },
    pendingWarnRow: {
      background: theme.palette.warning.selected,
      "&:hover": {
        background: `${theme.palette.warning.focusVisible} !important`
      }
    },
    vehicleText: {
      color: theme.palette.text.secondary
    },
    tag: {
      borderRadius: theme.spacing(1),
      fontWeight: 500
    },
    truncate: {
      maxWidth: "100%",
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      minWidth: 0
    },
    constrainedStack: {
      width: "100%",
      overflow: "hidden"
    }
  })
);


export default function useDispatchColumns() {

  const { classes } = useStyles();
  const { t } = useTranslation();
  const { station } = useStation();

  return useMemo(() => {
    return [
      {
        field: "number",
        headerName: t("dispatch:table:taskNumber"),
        type: "number",
        valueGetter: (_, row) => row.missionNumber + row.number / 10.0,
        valueFormatter: (_, row) => `${row.missionNumber}.${row.number}`,
        renderCell: (params) => <Typography variant="caption" color="textSecondary">{params.value}</Typography>,
        align: "center",
        flex: 0,
      },

      {
        field: "urgency",
        headerName: t("dispatch:table:urgency"),
        type: "singleSelect",
        valueOptions: [
          { value: TaskEnums.EstimatedUrgency.Critical, label: t(`task:enums:estimatedUrgency:Critical`) },
          { value: TaskEnums.EstimatedUrgency.High, label: t(`task:enums:estimatedUrgency:High`) },
          { value: TaskEnums.EstimatedUrgency.Normal, label: t(`task:enums:estimatedUrgency:Normal`) },
          { value: TaskEnums.EstimatedUrgency.Low, label: t(`task:enums:estimatedUrgency:Low`) },
          { value: TaskEnums.EstimatedUrgency.None, label: t(`task:enums:estimatedUrgency:None`) },
        ],
        valueGetter: (_, row) => row.urgency,
        renderCell: params => <UrgencyColumn task={params.row} />,
        flex: 1,
        minWidth: 240,
      },

      {
        field: "priority",
        headerName: t("dispatch:table:priority"),
        type: "singleSelect",
        valueOptions: [
          { value: TaskEnums.Priority.High, label: t(`task:enums:priority:High`) },
          { value: TaskEnums.Priority.Normal, label: t(`task:enums:priority:Normal`) },
          { value: TaskEnums.Priority.Low, label: t(`task:enums:priority:Low`) },
        ],
        valueGetter: (_, row) => row.priority,
        renderCell: params => <Chip
          size="small"
          variant={params.value === TaskEnums.Priority.Low ? "outlined" : "filled"}
          label={params.formattedValue}
          color={params.value === TaskEnums.Priority.High ? "primary" : "default"}
          className={classes.tag}
        />,
        flex: 0.5,
        minWidth: 80,
      },

      {
        field: "driver",
        flex: 1,
        minWidth: 240,
        headerName: t("common:tables:driver"),
        valueGetter: (_, row) => row.execution?.driver?.name || "",
        renderCell: params => <DriverColumn task={params.row} />
      },

      {
        field: "state",
        headerName: t("common:tables:state"),
        type: "singleSelect",
        valueOptions: [
          { value: TaskEnums.State.None, label: t(`task:enums:state:None`) },
          { value: TaskEnums.State.Started, label: t(`task:enums:state:Started`) },
          { value: TaskEnums.State.Arrived, label: t(`task:enums:state:Arrived`) },
          station?.includeArrivedDest ? { value: TaskEnums.State.ArrivedDest, label: t(`task:enums:state:ArrivedDest`) } : null,
          { value: TaskEnums.State.Loaded, label: t(`task:enums:state:Loaded`) },
          { value: TaskEnums.State.Completed, label: t(`task:enums:state:Completed`) },
        ].filter(r => !!r),
        valueGetter: (_, row) => row.state < TaskEnums.State.Started ? TaskEnums.State.None : row.state > TaskEnums.State.Completed ? TaskEnums.State.Completed : row.state,
        renderCell: params => <TaskProgressColumn task={params.row} station={station} value={params.value} />,
        flex: 1,
        minWidth: 220,
      },

      {
        field: "created",
        headerName: t("common:tables:created"),
        type: "dateTime",
        valueGetter: (_, row) => new Date(row.execution.history.created?.created),
        renderCell: (params) => <TimeView time={params.row.execution.history.created?.created} refresh={Date.now()} />,
        flex: 0,
        minWidth: 160,
      },
      {
        field: "type",
        headerName: t("common:tables:type"),
        flex: 1,
        minWidth: 120,
        type: "singleSelect",
        valueGetter: (_, row) => row.missionDetails.type,
        valueFormatter: (value) => t(`mission:enums:type:${MissionEnumsLists.Type[value]}`),
        valueOptions: MissionEnumsLists.Type.map((_, value) => ({
          value,
          label: t(`mission:enums:type:${MissionEnumsLists.Type[value]}`)
        }))
      },
      {
        field: "cause",
        headerName: t("common:tables:cause"),
        valueGetter: (_, row) => row.missionDetails?.cause?.name || "-",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "details.tags",
        headerName: t("common:tables:tags"),
        valueGetter: (_, row) => row.missionDetails?.tags?.map(t => t.name).join() || "",
        flex: 0.8,
        minWidth: 100,
        renderCell: params => (
          <Grid container spacing={0.5}>
            {params.row.missionDetails?.tags?.map((tag, key) => <Grid item key={key}><Chip label={tag.name} size="small" className={classes.tag} /></Grid>)}
          </Grid>
        )
      },
      {
        field: "object",
        headerName: t("common:tables:object"),
        valueGetter: (_, row) => {
          const type = row.missionTarget.type;
          const details = type === 1 ? row.missionTarget.carDetails : (type === 2 ? row.missionTarget.boatDetails : null);
          const description = row.missionTarget.description || "";
          return details ? `${details.make || ""} ${details.model || ""}` : truncate(description);
        },
        renderCell: params => {
          const row = params.row;
          const type = row.missionTarget.type;
          const details = type === 1 ? row.missionTarget.carDetails : (type === 2 ? row.missionTarget.boatDetails : null);

          return (
            <Stack>
              <Typography variant="body2" fontWeight={500}>{params.value}</Typography>
              {
                !!details ? (<Typography variant="caption" color="textSecondary">{details.registration}</Typography>) : null
              }
            </Stack>
          )
        },
        minWidth: 200,
        flex: 1
      },
      {
        field: "registration",
        headerName: t("entity:mission:target:registration"),
        valueGetter: (_, row) => {
          const type = row.missionTarget.type;
          const details = type === 1 ? row.missionTarget.carDetails : (type === 2 ? row.missionTarget.boatDetails : null);
          return details?.registration || "-"
        },
        minWidth: 100,
        flex: 1
      },
      {
        field: "address",
        headerName: t("common:tables:address"),
        valueGetter: (_, row) => FormatLocationShort(row.route.waypoints[0]),
        renderCell: params => {
          const wp = params.row.route.waypoints[0];
          return (
            <Stack className={classes.constrainedStack}>
              <Typography variant="body2" fontWeight={500}>{wp?.place}</Typography>
              <Typography variant="caption" color="textSecondary" className={classes.truncate}>{wp?.title || wp?.address}</Typography>
            </Stack>
          );
        },
        minWidth: 180,
        flex: 1
      },
      {
        field: "description",
        headerName: t("common:tables:locationDescription"),
        valueFormatter: (value) => value.length > 40 ? `${value.substring(0, 39)} ...` : value,
        minWidth: 100,
        flex: 1
      },
      {
        field: "transportTo",
        headerName: t("common:tables:transportTo"),
        valueGetter: (_, row) => {
          const list = row.route.waypoints;

          if (!list || list.length < 2) {
            return "-";
          } else {
            const location = list[list.length - 1];
            return FormatLocationShort(location);
          }
        },
        renderCell: params => {

          const list = params.row.route.waypoints;

          if (params.row.resolvedAtLocation) {
            return <Chip label={t("dispatch:table:resolvedAtLocation")} size="small" className={classes.tag} />
          } else if (!list || list.length < 2) {
            return (<Typography variant="body2" fontWeight={500}>-</Typography>)
          }

          const wp = list[list.length - 1];

          return (
            <Stack className={classes.constrainedStack}>
              <Typography variant="body2" fontWeight={500}>{wp?.place}</Typography>
              <Typography variant="caption" color="textSecondary" className={classes.truncate}>{wp?.title || wp?.address}</Typography>
            </Stack>
          );
        },
        minWidth: 180,
        flex: 1
      },
      {
        field: "details",
        headerName: t("common:tables:details"),
        valueGetter: (_, row) => row.missionDetails.description || "-",
        valueFormatter: (value) => value.length > 40 ? value.substring(0, 38) + "..." : value,
        flex: 1,
        minWidth: 140
      },
      {
        field: "eta",
        headerName: t("common:tables:eta"),
        type: "dateTime",
        valueGetter: (_, row) => !!row.execution.estimatedArrival ? new Date(row.execution.estimatedArrival) : null,
        renderCell: (params) => <TimeView time={params.row.execution.estimatedArrival} refresh={Date.now()} />,
        flex: 1,
        minWidth: 100,
      },

    ]
  }, [t, classes, station]);
}